import { AlertTriangle, BookOpen, Fingerprint, KeyRound, LifeBuoy } from "lucide-react";
import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";

const SECTIONS = [
  ["keys", "The two keys"],
  ["setup", "Setting up"],
  ["protection", "How your data is protected"],
  ["modes", "Who can open what"],
  ["lost", "If something is lost"],
  ["checklist", "What to keep safe"],
  ["glossary", "Glossary"],
] as const;

function H({ id, icon, children }: { id: string; icon?: React.ReactNode; children: string }) {
  return (
    <h2 id={id} className="guide-heading">
      {icon}
      {children}
    </h2>
  );
}

export function Guide() {
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) return;
    document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [hash]);

  return (
    <div className="guide">
      <h1 className="guide-title">
        <BookOpen size={24} className="icon" />
        How Tether works
      </h1>
      <p className="lead">
        Tether streams location and audio from a phone into storage that you own. Everything
        sensitive is locked on the phone before it leaves, so the gateway and your storage provider
        only ever hold scrambled data. Two different keys do two different jobs. Please read the
        first section before anything else.
      </p>
      <nav className="guide-toc" aria-label="On this page">
        {SECTIONS.map(([id, label]) => (
          <a key={id} href={`#${id}`}>
            {label}
          </a>
        ))}
      </nav>

      <H id="keys" icon={<KeyRound size={20} className="icon" />}>
        The two keys
      </H>
      <p>
        In Tether the word <strong>passkey</strong> always means the thing that signs you in, and{" "}
        <strong>decryption key</strong> always means the thing that opens your data. They are
        independent: signing in does not give you the decryption key, and the decryption key does
        not sign you in. You need both to see anything.
      </p>
      <div className="key-grid">
        <div className="card key-card">
          <h3>
            <Fingerprint size={18} className="icon" />
            Passkey: signs you in
          </h3>
          <ul>
            <li>
              Unlocks this gateway with your fingerprint, face, or device PIN. There is no password.
            </li>
            <li>
              Stored by your device or password manager (iCloud Keychain, Google Password Manager,
              1Password, a security key). Tether never sees it.
            </li>
            <li>
              It only signs you in. It does <strong>not</strong> open recordings or location
              history.
            </li>
            <li>
              <strong>If you lose it:</strong> you cannot sign in. Tether has no built-in recovery
              for a lost passkey today, so use one that syncs. See{" "}
              <a href="#lost">If something is lost</a>.
            </li>
          </ul>
        </div>
        <div className="card key-card">
          <h3>
            <KeyRound size={18} className="icon" />
            Decryption key: opens your data
          </h3>
          <ul>
            <li>
              Created automatically in this browser the first time you need it. It never leaves this
              browser and is never sent to the gateway.
            </li>
            <li>
              Opens what was sealed to you: the location history and, depending on the vault, the
              recordings.
            </li>
            <li>
              Clearing this browser's data, or switching browsers, removes it. That is why you back
              it up from <Link to="/settings">Settings</Link> into a password manager.
            </li>
            <li>
              <strong>If you lose it and have no backup:</strong> nobody can recover it for you,
              including us. What was sealed to you stays sealed.
            </li>
          </ul>
        </div>
      </div>
      <p className="muted">
        Other secrets exist, but they belong to whoever runs the gateway: the one-time{" "}
        <strong>setup token</strong> printed when the gateway first starts, the{" "}
        <strong>storage credentials</strong> (created for you when you use Cloudflare R2), and a{" "}
        <strong>pairing code</strong> that links a phone once and then expires. Phones hold no
        decryption key: a phone can lock data, but it cannot open it.
      </p>

      <H id="setup">Setting up</H>
      <ol className="guide-steps">
        <li>
          <strong>Choose a mode.</strong> Who is being protected, and who may open the recordings
          (see <a href="#modes">Who can open what</a>).
        </li>
        <li>
          <strong>Choose storage.</strong> Cloudflare R2 is recommended: paste one token and Tether
          creates the bucket for you. A folder on this machine also works, but it is unreliable. If
          the machine is lost or offline, nothing is stored and there is no backup.
        </li>
        <li>
          <strong>Create your passkey.</strong> Use one that syncs between your devices (iCloud
          Keychain, Google Password Manager, or a password manager), because a lost sole passkey
          cannot be recovered.
        </li>
        <li>
          <strong>Create a vault.</strong> A vault is the protected person's stream of data. Give it
          their name.
        </li>
        <li>
          <strong>Save your decryption key.</strong> A card on the Vaults page asks you to copy it
          into a password manager and confirm. Do this now, not later.
        </li>
        <li>
          <strong>Pair a phone.</strong> Open the vault, choose <em>Manage</em>, then{" "}
          <em>Pair a device</em>. In the phone app, enter the gateway address and the pairing code.
          The address must be one the phone can reach, for example your computer's address on the
          same Wi-Fi.
        </li>
        <li>
          <strong>Record and check.</strong> Start recording on the phone, then open{" "}
          <em>Timeline</em> to see points appear.
        </li>
      </ol>

      <H id="protection">How your data is protected</H>
      <ul className="guide-list">
        <li>
          <strong>Locked on the phone.</strong> Audio and location are sealed before they upload.
          The gateway and your storage provider see only scrambled data.
        </li>
        <li>
          <strong>Location and events can be read quickly.</strong> People who hold a decryption key
          for the vault (the owner and, where used, confidants) can read where and when, so loved
          ones can start searching straight away.
        </li>
        <li>
          <strong>Recordings are locked more tightly.</strong> They open only through the vault's
          rule, below. Even the owner cannot bypass it.
        </li>
        <li>
          <strong>One time window at a time.</strong> Each short window of recording has its own
          lock, so opening one period does not open the others.
        </li>
        <li>
          <strong>Everything is logged.</strong> Each unlock is written to a signed audit log. In a
          kid vault, the child's phone is also told when recordings are opened.
        </li>
      </ul>

      <H id="modes">Who can open what</H>
      <table className="guide-table">
        <thead>
          <tr>
            <th>Mode</th>
            <th>Opens the recordings</th>
            <th>Must keep safe</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <strong>Kid</strong>
            </td>
            <td>The parent who owns the vault, on their own.</td>
            <td>
              The parent's passkey and decryption key. The child's phone holds nothing secret.
            </td>
          </tr>
          <tr>
            <td>
              <strong>Guardian</strong>
            </td>
            <td>
              One chosen co-owner approves. The owner can read location history but cannot open
              recordings alone.
            </td>
            <td>The co-owner's passkey and decryption key, and the owner's own pair.</td>
          </tr>
          <tr>
            <td>
              <strong>Circle</strong>
            </td>
            <td>A set number of confidants (for example 3 of 5) must approve.</td>
            <td>Every confidant keeps their own passkey and decryption key.</td>
          </tr>
        </tbody>
      </table>

      <H id="lost" icon={<AlertTriangle size={20} className="icon" />}>
        If something is lost
      </H>
      <table className="guide-table">
        <thead>
          <tr>
            <th>What happened</th>
            <th>What it means</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <strong>Lost phone</strong> (the capturing device)
            </td>
            <td>
              Nothing secret was on it, and what it already uploaded stays safe. Revoke it under the
              vault's Devices, then pair a new one.
            </td>
          </tr>
          <tr>
            <td>
              <strong>Lost passkey</strong>, and it syncs
            </td>
            <td>Nothing is lost. Sign in on the new device with the same synced passkey.</td>
          </tr>
          <tr>
            <td>
              <strong>Lost passkey</strong>, your only one, not synced
            </td>
            <td>
              You cannot sign in, and Tether has no built-in recovery for this today. Your data
              stays in your storage, still sealed, but you cannot reach it through the app. The best
              protection is a passkey that syncs.
            </td>
          </tr>
          <tr>
            <td>
              <strong>Lost decryption key</strong>, but you have a backup
            </td>
            <td>
              Fully recoverable. In <Link to="/settings">Settings</Link>, paste the backup and press
              Restore.
            </td>
          </tr>
          <tr>
            <td>
              <strong>Lost decryption key</strong>, no backup, kid vault
            </td>
            <td>
              <strong>Permanent.</strong> The parent is the only one who could open it, so the
              recordings and location history can never be read again.
            </td>
          </tr>
          <tr>
            <td>
              <strong>Lost decryption key</strong>, no backup, guardian or circle vault
            </td>
            <td>
              Guardian: recordings stay openable while the co-owner still has theirs. If the
              co-owner's is the one lost, they cannot be opened (the owner can still read location
              history). Circle: it keeps working while enough confidants remain, and recordings are
              lost for good only if too many keys are gone at once.
            </td>
          </tr>
          <tr>
            <td>
              <strong>Cleared browser data or a new browser</strong>
            </td>
            <td>
              Your decryption key is gone from that browser. Restore it from your backup in
              Settings.
            </td>
          </tr>
          <tr>
            <td>
              <strong>Lost the machine running the gateway</strong>
            </td>
            <td>
              Your recordings and account records live in your storage, not on the gateway. Run a
              new gateway against the same storage.
            </td>
          </tr>
        </tbody>
      </table>

      <H id="checklist" icon={<LifeBuoy size={20} className="icon" />}>
        What to keep safe
      </H>
      <ul className="guide-list">
        <li>
          <strong>Passkey:</strong> keep it in a keychain or password manager that syncs. Check it
          shows up on a second device.
        </li>
        <li>
          <strong>Decryption key:</strong> save it as a secure note or password entry in a password
          manager. Not in a plain notes app, an email, or a screenshot, and remember that clipboard
          managers can keep what you copy.
        </li>
        <li>
          <strong>Never share either one.</strong> Tether will never ask you for your decryption
          key.
        </li>
        <li>
          <strong>Setup token and storage tokens:</strong> only for whoever runs the gateway.
        </li>
      </ul>

      <H id="glossary">Glossary</H>
      <dl className="glossary">
        <dt>Gateway</dt>
        <dd>
          The server that receives the data and serves this web app. It cannot read what it stores.
        </dd>
        <dt>Vault</dt>
        <dd>One protected person's data, with its own rules for who may open it.</dd>
        <dt>Confidant</dt>
        <dd>A trusted person who holds a piece of a Circle vault and can approve an unlock.</dd>
        <dt>Co-owner</dt>
        <dd>The single approver in a Guardian vault.</dd>
        <dt>Location history (metadata)</dt>
        <dd>Where, when, which device, and trigger events. Readable by key holders at any time.</dd>
        <dt>Recordings (media)</dt>
        <dd>Audio, sealed behind the vault's unlock rule.</dd>
        <dt>Pairing code</dt>
        <dd>A one-time code that links a phone to a vault. It expires.</dd>
        <dt>Setup token</dt>
        <dd>
          A one-time secret printed by the gateway, proving you are the person who deployed it.
        </dd>
      </dl>
    </div>
  );
}
